import { useState, useEffect } from 'react';
import { X, Download, ExternalLink, Loader2 } from 'lucide-react';

const MIME_FROM_EXT = {
    pdf: 'application/pdf',
    jpg: 'image', jpeg: 'image', png: 'image', webp: 'image', gif: 'image',
    txt: 'text/plain', csv: 'text/plain',
    html: 'text/html',
    docx: 'docx', doc: 'docx',
};

function getType(pathOrName) {
    if (!pathOrName) return 'unknown';
    // Pour les blob URLs (blob:http://…), `src` n'a pas d'extension exploitable.
    // L'appelant doit alors fournir `name` (filename original) en fallback.
    const last = pathOrName.split('.').pop().toLowerCase();
    return MIME_FROM_EXT[last] || 'unknown';
}

/**
 * DocumentViewer — fullscreen modal to preview documents inline
 * Props:
 *   src: string (URL path to document)
 *   name: string (filename for display)
 *   onClose: () => void
 */
export default function DocumentViewer({ src, name, onClose }) {
    const [textContent, setTextContent] = useState(null);
    const [docxHtml, setDocxHtml] = useState(null);
    const [docxError, setDocxError] = useState(null);
    // Privilégie `name` (filename original) pour la détection ; fallback `src`.
    // Permet les blob URLs (où src n'a pas d'extension utile).
    const type = getType(name) !== 'unknown' ? getType(name) : getType(src);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    useEffect(() => {
        if (type === 'text/plain') {
            fetch(src).then(r => r.text()).then(setTextContent).catch(() => setTextContent('Erreur de chargement'));
        }
    }, [src, type]);

    // Rendu DOCX via mammoth.js (dynamic import : ne charge la lib ~150KB
    // que si l'utilisateur ouvre effectivement un .docx).
    useEffect(() => {
        if (type !== 'docx') return;
        let cancelled = false;
        setDocxHtml(null);
        setDocxError(null);
        (async () => {
            try {
                const [{ default: mammoth }, response] = await Promise.all([
                    import('mammoth/mammoth.browser.min.js'),
                    fetch(src, { credentials: 'include' }),
                ]);
                if (!response.ok) throw new Error('HTTP ' + response.status);
                const arrayBuffer = await response.arrayBuffer();
                const result = await mammoth.convertToHtml({ arrayBuffer });
                if (!cancelled) setDocxHtml(result.value || '<p><em>Document vide</em></p>');
            } catch (e) {
                if (!cancelled) setDocxError(e.message || 'Erreur de conversion');
            }
        })();
        return () => { cancelled = true; };
    }, [src, type]);

    // Close on Escape
    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    return (
        <div className="docviewer-overlay" onClick={onClose}>
            <div className="docviewer" onClick={e => e.stopPropagation()}>
                <div className="docviewer__header">
                    <span className="docviewer__title">{name || src?.split('/').pop()}</span>
                    <div className="docviewer__actions">
                        <a href={src} download className="docviewer__btn" title="Télécharger">
                            <Download size={16} />
                        </a>
                        <a href={src} target="_blank" rel="noopener noreferrer" className="docviewer__btn" title="Ouvrir dans un nouvel onglet">
                            <ExternalLink size={16} />
                        </a>
                        <button className="docviewer__btn docviewer__btn--close" onClick={onClose} title="Fermer">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                <div className="docviewer__content">
                    {type === 'application/pdf' && (
                        <iframe src={src} className="docviewer__iframe" title={name} />
                    )}

                    {type === 'image' && (
                        <div className="docviewer__image-wrap">
                            <img src={src} alt={name} className="docviewer__image" />
                        </div>
                    )}

                    {type === 'text/plain' && (
                        <pre className="docviewer__text">{textContent ?? 'Chargement...'}</pre>
                    )}

                    {type === 'text/html' && (
                        <iframe src={src} className="docviewer__iframe" title={name} sandbox="allow-same-origin" />
                    )}

                    {type === 'docx' && !docxError && (
                        docxHtml === null
                            ? <div className="docviewer__fallback">
                                  <Loader2 size={20} className="spin" /> Conversion du document Word…
                              </div>
                            : <div className="docviewer__docx" dangerouslySetInnerHTML={{ __html: docxHtml }} />
                    )}

                    {type === 'docx' && docxError && (
                        <div className="docviewer__fallback">
                            <p>Impossible d'afficher ce document Word ({docxError}).</p>
                            <a href={src} download className="btn btn-primary">
                                <Download size={16} /> Télécharger le document
                            </a>
                        </div>
                    )}

                    {type === 'unknown' && (
                        <div className="docviewer__fallback">
                            <p>Ce format ne peut pas être affiché dans le navigateur.</p>
                            <a href={src} download className="btn btn-primary">
                                <Download size={16} /> Télécharger le document
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
